import React, {useEffect, useRef, useState} from "react";
import {useDebounce} from "@uidotdev/usehooks";

import {FilterTag} from "./FilterTag";
import {clearedSubFilters} from "./utils";
import {useShallow} from "zustand/react/shallow";
import {sortOptionsForMode, useListingsStore} from "../../stores/listingsStore";

export function SearchBar() {
    const inputRef = useRef(null);
    const wrapRef = useRef(null);
    const [open, setOpen] = useState(false);

    const {
        search,
        setSearch,
        filters,
        dealerMode,
        setFilters,
        fetchListings,
        suggestions,
        fetchSuggestions,
        clearSuggestions,
    } = useListingsStore(
        useShallow((state) => ({
            search: state.search,
            setSearch: state.setSearch,
            filters: state.filters,
            dealerMode: state.dealerMode,
            setFilters: state.setFilters,
            fetchListings: state.fetchListings,
            suggestions: state.suggestions,
            fetchSuggestions: state.fetchSuggestions,
            clearSuggestions: state.clearSuggestions,
        }))
    )

    const sortOptions = sortOptionsForMode(dealerMode)

    const filterTags = filters.searchTags

    // Search-bar tags mirror the vehicle hierarchy only: make/model (from
    // searchTags) plus the Year multi-select. Trim (versions) and Engine live in the
    // active-filter ribbon below the bar, so they are NOT shown here — that keeps a
    // single chip per selection and no duplication. Removing a tag clears that
    // filter — which also unchecks its sidebar control (they share state).
    // Removing Make or Model changes the vehicle being searched, which invalidates
    // every sidebar facet with it — a Kia's trims, colors, options and features mean
    // nothing once the make is gone — so the subfilters reset in the SAME update,
    // using the same payload the ribbon's "Clear all" applies. Make is the parent of
    // Model, so dropping it drops Model too. Year is a multi-select that sits above
    // this hierarchy (and is its own sidebar facet), so it is deliberately preserved
    // — see removeFromList, which is unchanged.
    // One setFilters call = one coordinated update = one request.
    const removeSearchTag = (cat) => {
        const newTags = {...filters.searchTags}
        delete newTags[cat]
        if (cat === 'make') delete newTags.model
        setFilters({searchTags: newTags, car: null, ...clearedSubFilters()}, true)
    }

    // Search-bar "Clear all": the full vehicle reset — Year/Make/Model, the exact-trim
    // `car` pin and every subfilter, in one update. Sort, zip/radius, dealer scope and
    // view mode are untouched, matching what the ribbon's and the sidebar's "Clear all"
    // already refuse to reset.
    const clearAllSearch = () => {
        setFilters({searchTags: {}, car: null, ...clearedSubFilters({includeYears: true})}, true)
        setSearch('')
        clearSuggestions()
        setOpen(false)
    }

    const removeFromList = (field, value) => {
        // Match the sidebar toggle (single facet toggle → skip/keep that facet's
        // own counts) so tag-remove and un-checking behave identically.
        setFilters({[field]: (filters[field] || []).filter((v) => v !== value)}, false, field)
    }
    const tags = [
        ...['make', 'model'].filter((k) => filterTags[k]).map((k) => ({
            key: k, category: k, value: filterTags[k], onRemove: () => removeSearchTag(k),
        })),
        ...(filters.years || []).map((y) => ({
            key: `year-${y}`, category: 'year', value: y, onRemove: () => removeFromList('years', y),
        })),
    ]

    // Prepend the active make/model (and a single selected year) so suggestions
    // stay scoped to the selected vehicle — but NOT the trim, so the user can
    // still discover other trims while typing. With multiple years selected we
    // drop the year from the query rather than over-constrain the suggestions.
    const buildSuggestQuery = (typed) => {
        const tags = filterTags || {}
        const years = filters.years || []
        const yearPart = years.length === 1 ? years[0] : null
        return [yearPart, tags.make, tags.model, typed]
            .filter(Boolean)
            .join(' ')
            .trim()
    }

    // Fetch suggestions as the user types (debounced). Clear when too short.
    const debouncedSearch = useDebounce(search, 250)
    useEffect(function loadSuggestions(){
        const typed = debouncedSearch.trim()
        if (typed.length >= 2) {
            fetchSuggestions(buildSuggestQuery(typed))
        } else {
            clearSuggestions()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch])

    // Close the dropdown when clicking outside the search bar.
    useEffect(function closeOnOutsideClick(){
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    const selectSuggestion = (s) => {
        // Model-level search: make/model are the base tags; the year goes into the
        // multi-select `years` (so it shows as a removable tag and more years can
        // be added, matching the sidebar). No trim / no `car` (which would pin the
        // exact trim config server-side).
        const searchTags = {make: s.make, model: s.model}
        // body_types holds style labels valid for one vehicle only, so it can't carry over.
        setFilters({searchTags, years: [String(s.year)], car: null, body_types: []}, true)
        setSearch('')
        clearSuggestions()
        setOpen(false)
    }

    const showSuggestions = open && suggestions.length > 0

    // Keep Enter local to the search field. The Calculator page registers a global
    // document-level keydown listener (useEnterEffect in CalcPageInfo) that creates
    // a Lease quote on Enter. This is NOT a form submit (there is no form) — it's a
    // bubbling keydown reaching that document listener — so stopping propagation is
    // the correct, minimal fix. Bound to this input's own handler, so it fires only
    // while the search field is focused — empty or not (an empty field must not let
    // Enter fall through and fire the Lease modal). Enter with focus anywhere else
    // on the page is untouched.
    // Empty-input Backspace removes one search-bar chip per press. Priority clears
    // the most specific chip first — the Year, then the model, and the make last
    // (make/model define the whole search). Trim/engine are ribbon chips now, so
    // they are not in this list. Within a category the visually-last selected value
    // goes first. Reuses each chip's own `onRemove`, so a Backspace removal is
    // byte-for-byte identical to clicking that chip's ✕ (same setFilters payload,
    // refetch, and sidebar-checkbox sync). Returns true when a chip was removed.
    const removeLastChipByPriority = () => {
        const priority = ['year', 'model', 'make']
        for (const cat of priority) {
            const matches = tags.filter((t) => t.category === cat)
            if (matches.length > 0) {
                matches[matches.length - 1].onRemove()
                return true
            }
        }
        return false
    }

    const handleSearchKeyDown = (e) => {
        // Only when the field is empty — so normal text deletion (a non-empty
        // input) is never intercepted and never preventDefault-ed.
        if (e.key === 'Backspace' && e.target.value === '') {
            if (removeLastChipByPriority()) e.preventDefault()
            return
        }
        if (e.key !== 'Enter') return
        // Stop the native event before it reaches the CalcPage's document listener
        // (belt-and-suspenders: stopImmediatePropagation too).
        e.preventDefault()
        e.stopPropagation()
        e.nativeEvent?.stopImmediatePropagation?.()

        if (showSuggestions && suggestions.length > 0) {
            // Apply the top match (which sets the vehicle filters + refetches).
            selectSuggestion(suggestions[0])
        } else {
            // No dropdown — just re-run the current search.
            fetchListings()
            setOpen(false)
        }
    }

    return (
        <div className="filter-bar">
            <div className="filter-bar-inner">
                <div className="search-bar-wrap" ref={wrapRef}>
                    <div className="search-bar" onClick={() => inputRef.current?.focus()}>
              <span className="search-icon">
                <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                </svg>
              </span>
                        {tags.map((t) => (
                            <FilterTag key={t.key} category={t.category} value={t.value} onRemove={t.onRemove}/>
                        ))}
                        {
                            // Sits directly after the chips it clears — the same
                            // grouping the ribbon below uses — so it wraps along with
                            // them rather than anchoring to the bar's right edge.
                            // Reuses the ribbon's clear-all pill styling so the two
                            // controls read as the same action; the click also bubbles
                            // to .search-bar's onClick, which returns focus to the input.
                            tags.length > 0 &&
                            <button
                                type="button"
                                className="filter-chip-clear-all search-bar-clear"
                                aria-label="Clear all search filters"
                                onClick={clearAllSearch}
                            >
                                Clear all
                            </button>
                        }
                        <input
                            ref={inputRef}
                            className="search-text-input"
                            type="text"
                            placeholder="Search by year, make, model…"
                            value={search}
                            onFocus={() => setOpen(true)}
                            onChange={(e) => { setSearch(e.target.value); setOpen(true) }}
                            onKeyDown={handleSearchKeyDown}
                        />
                    </div>
                    {
                        showSuggestions &&
                        <div className="search-suggestions">
                            {suggestions.map((s, i) => (
                                <button
                                    key={`${s.label}-${i}`}
                                    type="button"
                                    className="search-suggestion"
                                    // mousedown (not click) so it fires before the input blur.
                                    onMouseDown={(e) => { e.preventDefault(); selectSuggestion(s) }}
                                >
                                    {s.label}
                                </button>
                            ))}
                        </div>
                    }
                </div>
                <div className="sort-wrap">
                    <label>Sort by</label>
                    <select
                        className="sort-select"
                        value={filters.sort_by}
                        onChange={e => setFilters({sort_by: e.target.value})}
                    >
                        {
                            sortOptions.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))
                        }
                    </select>
                </div>
            </div>
        </div>
    );
}
